/**
 * Substitutes `$content[identifier]` placeholders in admin-authored HTML
 * with the resolved CMS-block content the host plugin supplied via
 * {@link ConfiguratorPreviewContext.cmsBlocks}.
 *
 * Block VALUES are expected base64-encoded — exact mirror of legacy
 * `addContentBlock(id, base64encoded)` / `storage.js parseContentBlocks`,
 * which calls `Base64.decode(block)` before splice-in. Without that
 * encoding any `</script>` tag in a merchant's CMS-block HTML would
 * close the bootstrap script tag prematurely and dump the rest of the
 * block as visible page HTML.
 *
 * Identifier charset stays `[0-9a-zA-Z_\-]` to match the legacy regex.
 * Unresolved placeholders are left as-is (admin-preview without a plugin
 * has no block map; the merchant authoring sees the raw `$content[...]`
 * token which is the documented contract).
 *
 * Pure function — no React, no DOM. Safe to call during static render.
 */
const PLACEHOLDER_RE = /\$content\[([0-9a-zA-Z_-]+)\]/g;

export function substituteContentBlocks(
    html: string,
    blocks: Record<string, string> | undefined,
): string {
    if (!html || html.indexOf('$content[') === -1) return html;
    if (!blocks) return html;
    return html.replace(PLACEHOLDER_RE, (match, identifier: string) => {
        const encoded = blocks[identifier];
        // Leave the placeholder intact when the identifier is unknown.
        // The legacy bundle did the same — silently dropping it would
        // mask broken authoring; keeping the token surfaces the typo.
        if (encoded === undefined) return match;
        try {
            return decodeBase64Utf8(encoded);
        } catch {
            // Bad encoding (test fixtures that pass raw HTML, malformed
            // server response, …) → leave the placeholder visible so
            // the typo surfaces rather than silently dropping content.
            return match;
        }
    });
}

/**
 * Decode a base64 string the way the legacy storefront's `Base64.decode`
 * did — UTF-8 aware so non-ASCII merchant content (Umlaute, emoji, …)
 * survives the round trip. `atob` alone produces a binary string and
 * mangles multi-byte sequences.
 */
function decodeBase64Utf8(b64: string): string {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
}
